import { useMemo, useCallback } from "react";
import { FlatList } from "react-native";
import { useLocalSearchParams, usePathname, useRouter } from "expo-router";
import { useUnistyles, UnistylesRuntime } from "react-native-unistyles";
import { View, Text } from "@src/components";
import { sizes } from "@src/constants/sizes";
import type { ScenarioRow, ScenarioSiteGroup } from "../utils/scenario-group";
import { ScenarioCard } from "./ScenarioCard";

interface ScenarioGridProps {
  groups: ScenarioSiteGroup[];
  selectedSiteName?: string | null;
}

const COLUMN_COUNT = { xs: 1, sm: 2, md: 3, lg: 3, xl: 3 } as const;

export function ScenarioGrid({ groups, selectedSiteName }: ScenarioGridProps) {
  const { theme } = useUnistyles();
  const router = useRouter();
  const pathname = usePathname();
  const { selectedScenario } = useLocalSearchParams<{ selectedScenario?: string }>();
  const breakpoint = UnistylesRuntime.breakpoint as keyof typeof COLUMN_COUNT;
  const numColumns = COLUMN_COUNT[breakpoint] ?? COLUMN_COUNT.xs;
  const gridSpacing = theme.spacing["6xl"];
  const showTitle = !!selectedSiteName;

  const rows: ScenarioRow[] = useMemo(() => groups.flatMap((g) => g.rows), [groups]);

  const renderItem = useCallback(
    (info: { item: ScenarioRow | undefined }) => {
      if (!info.item) {
        return (
          <View
            style={{
              width: `${100 / numColumns}%`,
              paddingHorizontal: gridSpacing / 2,
              paddingVertical: gridSpacing / 2,
            }}
          />
        );
      }
      const row = info.item;
      return (
        <View
          style={{
            width: `${100 / numColumns}%`,
            paddingHorizontal: gridSpacing / 2,
            paddingVertical: gridSpacing / 2,
          }}
        >
          <ScenarioCard
            scenarioName={row.scenarioName}
            cameraDisplayName={row.cameraDisplayName}
            cameraSubtitle={row.cameraSubtitle}
            isSelected={selectedScenario === row.scenarioUuid}
            onPress={() => {
              const next =
                selectedScenario === row.scenarioUuid ? pathname : `${pathname}?selectedScenario=${row.scenarioUuid}`;
              router.replace(next as never);
            }}
          />
        </View>
      );
    },
    [gridSpacing, numColumns, pathname, router, selectedScenario],
  );

  return (
    <View flex={1} style={{ padding: theme.spacing["6xl"] }}>
      <View
        width="100%"
        maxWidth={sizes.maxWidthWeb}
        gap={showTitle ? "6xl" : undefined}
        pb="6xl"
        style={{ alignSelf: "center", zIndex: 10 }}
      >
        {showTitle && (
          <Text textStyle="lg/bold" color="text">
            {selectedSiteName} Scenarios
          </Text>
        )}
      </View>

      <View flex={1} alignItems="center">
        {rows.length === 0 ?
          <View flex={1} justifyContent="center" alignItems="center" py="13xl">
            <Text textStyle="md/regular" color="textMuted">
              No scenarios available
            </Text>
          </View>
        : <FlatList
            key={numColumns}
            data={rows}
            renderItem={renderItem}
            keyExtractor={(item, index) => item?.key ?? `empty-${index}`}
            numColumns={numColumns}
            showsVerticalScrollIndicator={false}
            windowSize={5}
            initialNumToRender={numColumns * 4}
            removeClippedSubviews
            style={{ width: "100%", maxWidth: sizes.maxWidthWeb }}
            contentContainerStyle={{
              paddingHorizontal: gridSpacing / 2,
              paddingBottom: theme.spacing["8xl"],
            }}
          />
        }
      </View>
    </View>
  );
}
